import React, { useState } from 'react';
import { useFormContext, Controller } from 'react-hook-form';
import { useTheme } from '@mui/material/styles';
import { Box, IconButton, useMediaQuery, TextField, Grid, Divider } from '@mui/material';
import { IResponsePersonnel } from 'pages/EMSResponse/hooks/useResponseForm';
import { JobPositions } from 'common/selectoptions';
import { IRequestPersonnel } from 'interfaces/ResourceRequests';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import { InfoOutlined as Info } from '@mui/icons-material';
import useStyles, { TabsStyles, TabItemStyles } from './AcceptResources.styles';
import { Typo } from 'components/primitives';

const AcceptPersonnelItems: React.FC<{
  isDisabled: boolean;
  rootName: string;
  formName: string;
  field: IResponsePersonnel;
  mainIdx: number;
}> = ({ rootName, formName, field, mainIdx, isDisabled }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { control, getValues, setValue } = useFormContext();
  let values = getValues();
  values = values.requests ? values.requests : values;
  const ttlCountRef = React.useRef(values.personnel[mainIdx]?.items?.[0]?.number || 0);
  const ttlCount = ttlCountRef.current;
  const acceptedRef = React.useRef(values.personnel[mainIdx]?.filled || 0);
  const accepted = acceptedRef.current;
  const neededRef = React.useRef(values.personnel[mainIdx]?.number || 0);
  const needed = neededRef.current;
  const totalRef = React.useRef(0);
  const [total, setTotal] = useState<number>(accepted + ttlCount);
  const disableBtn = isDisabled ? isDisabled : ttlCount === 0;
  const countPersonnel = (values.personnel || []).reduce(
    (sum: number, item: IRequestPersonnel) =>
      sum + (typeof item.number === 'number' ? item.number : 0),
    0,
  );
  const classes = useStyles();
  const getFullTypeName = (type: string): string =>
    JobPositions.find((i) => i.value === type)?.label || '';

  React.useEffect(() => {
    if (field.items && (!field.items[0] || field.items[0].number === -1)) {
      setValue(`${rootName}.0.number`, ttlCount);
    }
  }, [ttlCount]);

  React.useEffect(() => {
    setValue(`${rootName}.0.accepted`, true);
  }, []);

  const handleDecrement = () => {
    let currentValue = getValues(`${rootName}.0.number`) || 0;
    currentValue = currentValue > 0 ? currentValue - 1 : 0;
    //console.log('currentValue', currentValue);
    setValue(`${rootName}.0.number`, currentValue);
    setTotal(accepted + currentValue);
    totalRef.current = accepted + currentValue;

    const supportPersonnelCount = (values.personnel || []).reduce(
      (sum: number, item: IRequestPersonnel) =>
        sum + (item.items && item.items[0].number ? item.items[0].number : 0),
      0,
    );
    setValue('supportPersonnelCount', supportPersonnelCount);
  };

  const handleIncrement = () => {
    let currentValue = getValues(`${rootName}.0.number`) || 0;
    currentValue = currentValue < ttlCount ? currentValue + 1 : ttlCount;
    setValue(`${rootName}.0.number`, currentValue);
    setTotal(accepted + currentValue);
    totalRef.current = accepted + currentValue;

    const supportPersonnelCount = (values.personnel || []).reduce(
      (sum: number, item: IRequestPersonnel) =>
        sum + (item.items && item.items[0].number ? item.items[0].number : 0),
      0,
    );
    setValue('supportPersonnelCount', supportPersonnelCount);
  };

  const onBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const currentValue = getValues(`${rootName}.0.number`) || 0;
    setValue(`${rootName}.0.number`, currentValue);
  };

  const onNumberChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const supportPersonnelCount = (values.personnel || []).reduce(
      (sum: number, item: IRequestPersonnel) =>
        sum + (item.items && item.items[0].number ? item.items[0].number : 0),
      0,
    );
    setValue('supportPersonnelCount', supportPersonnelCount);
  };

  return (
    <React.Fragment>
      <Box className={classes.grayBox}>
        <Grid container columnSpacing={1} rowSpacing={1}>
          <Grid item xs={12} lg={12}>
            <div
              style={{
                width: '100%',
                alignSelf: 'stretch',
                justifyContent: 'flex-start',
                alignItems: 'flex-start',
                //gap: '4px',
                display: 'inline-flex',
                paddingLeft: '4px',
                paddingRight: '12px',
              }}>
              <div
                style={{
                  width: '100%',
                  cursor: 'pointer',
                  alignSelf: 'center',
                  //height: '40px',
                  padding: '0 8px 0 8px',
                  background: '#ffffff',
                  overflow: 'hidden',
                  //borderRadius: '4px',
                  //outline: '1px var(--stroke-subtle-alt, #F4F6F7) solid',
                  //outlineOffset: '-1px',
                  //gap: '10px',
                  display: 'inline-flex',
                }}>
                <div
                  style={{
                    //marginTop: isMobile ? '2px' : '2px',
                    color: isDisabled ? '#A6B6B6' : '#0A1D40',
                    fontSize: '14px',
                    fontFamily: 'Roboto',
                    fontWeight: '600',
                    wordWrap: 'break-word',
                  }}>
                  {getFullTypeName(field.type)}
                </div>
              </div>
              <div
                style={{
                  display: 'inline-flex',
                  background: '#FFFFFF',
                  borderRadius: '4px',
                  alignSelf: 'center',
                  outline: '1px #D6EEFF solid',
                }}>
                <IconButton
                  disabled={isDisabled}
                  onClick={handleDecrement}
                  sx={{
                    padding: '0 0 0 6px',
                    backgroundColor: '#FFFFFF',
                    ':hover': {
                      bgcolor: '#FFFFFF', // theme.palette.primary.main
                    },
                  }}>
                  <RemoveIcon fontSize='small' />
                </IconButton>
                <div
                  style={{
                    width: '40px',
                  }}>
                  <Controller
                    name={`${rootName}.0.number`}
                    control={control}
                    render={({ field: { onChange, value }, fieldState: { error } }) => (
                      <TextField
                        sx={{
                          '& .MuiInputBase-input.Mui-disabled': {
                            color: '#A6B6B6', // Sets the text color for most browsers
                            WebkitTextFillColor: '#D6D6D6', // Ensures color is applied in WebKit browsers
                          },
                          '& .MuiInputBase-root': {
                            backgroundColor: isDisabled ? '#F4F6F7' : '#FFFFFF',
                            color: '#2961FD',
                            height: '32px',
                            width: '40px',
                            '& > fieldset': {
                              borderColor: 'transparent',
                              padding: '0 !important',
                              border: 'none',
                            },
                            '&:hover fieldset': {
                              borderColor: 'white',
                            },
                            '&.Mui-focused fieldset': {
                              borderColor: 'transparent',
                            },
                            '&.Mui-disabled fieldset': {
                              borderColor: 'white',
                            },
                          },
                        }}
                        disabled={isDisabled}
                        error={Boolean(error)}
                        value={value < 0 ? 0 : value <= ttlCount ? value : ttlCount}
                        onChange={(e) => {
                          onChange(e);
                          onNumberChange(e);
                        }}
                        onBlur={onBlur}
                        inputProps={{
                          pattern: '^(0|[1-9][0-9]*)$',
                          style: {
                            padding: 0,
                            minWidth: '40px',
                            fontSize: '16px',
                            fontFamily: 'Roboto',
                            fontWeight: '600',
                            textAlign: 'center',
                          },
                        }}
                      />
                    )}
                  />
                </div>
                <IconButton
                  disabled={isDisabled}
                  sx={{
                    padding: '0 6px 0 0',
                    backgroundColor: '#FFFFFF',
                    ':hover': {
                      bgcolor: '#FFFFFF', // theme.palette.primary.main
                    },
                  }}
                  onClick={handleIncrement}>
                  <AddIcon fontSize='small' />
                </IconButton>
              </div>
            </div>
          </Grid>
        </Grid>
      </Box>
    </React.Fragment>
  );
};

export default AcceptPersonnelItems;
